<template>
  <v-container v-if="!review">
    <div class="text-h4">Where does most of your circuit's traffic go?</div>
    <p class="mt-2">
      Interstate is the most common designation and is defaulted but please change
      to intrastate if needed
    </p>
    <v-radio-group v-model="selected" @change="setTrafficAnswer">
      <template v-slot:label>
        <div>
          {{ trafficLabel }}
          <MoreInfoToolTip :msg="trafficTypeDescription" />
        </div>
      </template>
      <v-radio class="traffic-radio" value="interstate">
        <template v-slot:label>
          <div>
            Mostly internet, national, or international <br><small>More than 10% of traffic crosses state lines - browsing the web, cloud services, video calls, email, or connecting to out-of-state offices. <br>(Applies for most businesses)</small> <span style="background-color: lightblue; color:blue;">Interstate</span>
          </div>
        </template>
      </v-radio>
      <v-radio class="traffic-radio" value="intrastate">
        <template v-slot:label>
          <div>
            Stays entirely within one state <br><small>90% or more of traffic stays within your state - like connecting 2 offices in the same city, or a local intranet <br>(Can apply for local government, utilities, and campus networks)</small> <span style="background-color: lightgreen; color:green;">Intrastate</span>
          </div>
        </template>
      </v-radio>
      <v-radio class="traffic-radio" value="unknown">
        <template v-slot:label>
          <div>
            I don't know for sure
          </div>
        </template>
      </v-radio>
      <v-radio class="traffic-radio" v-if="isUsfExempt" value="interstate exempt">
        <template v-slot:label>
          <div>
            Interstate exempt
            <i>
              We have your previous exemption waiver on file, and it can be reused.</i>
          </div>
        </template>
      </v-radio>

    </v-radio-group>

    <i>
      Circuits that are interstate in nature are subject to the standard federal universal
      charges. These charges undergo pricing review and changes quarterly
    </i>

    <!-- <v-text-field :label="initialLabel" maxlength="3" :value="getProductInfo().traffic_initials"
      @input="setTrafficInitials" :hint="getNote" persistent-hint></v-text-field> -->

    <ESignature v-model="traffic_signature" :accepted.sync="traffic_signature_certified"
      acceptText="By signing you confirm the accuracy of the information you have provided." />

  </v-container>
  <div v-else>
    <ReviewItem label="What type of traffic will be going across this point to point circuit?"
      :value="getProductInfo().traffic" />
    <ReviewItem label="Signature" :value="getProductInfo().traffic_signature" />
    <ReviewItem label="Signature Acceptance" :value="getProductInfo().traffic_signature_certified" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import MoreInfoToolTip from '@/components/menus/MoreInfoToolTip.vue';
import ReviewItem from '@/components/ReviewItem.vue';
import ESignature from '@/components/formfields/ESignature.vue';

export default {
  name: 'Traffic',
  props: {
    review: {
      required: false,
      type: Boolean,
    },
  },
  mounted() {
    if (this.getProductInfo()?.traffic == null && this.isRetail) {
      // refs AIF-92: default retail to intrastate
      this.selected = 'intrastate';
      this.setTrafficAnswer(this.selected);
    } else if (this.getProductInfo()?.traffic == null) {
      // refs AIF-92: default carrier/wholesale and anything else to interstate
      this.selected = 'interstate';
      this.setTrafficAnswer(this.selected);
    } else {
      this.selected = this.getProductInfo().traffic;
    }

    const productInfo = this.getProductInfo?.() || {};
    this.traffic_signature = productInfo.traffic_signature || '';
    this.traffic_signature_certified = productInfo.traffic_signature_certified || 'n';
  },
  methods: {
    ...mapActions(['updateStoreFormData']),
    setTrafficAnswer(newVal) {
      if (newVal === "unknown") {
        newVal = "interstate";
      }
      this.updateStoreFormData({
        data: newVal,
        main: this.getProductIdSelected,
        sub: 'traffic',
      });
    },
    setTrafficInitials(newVal) {
      this.updateStoreFormData({
        data: newVal,
        main: this.getProductIdSelected,
        sub: 'traffic_initials',
      });
      let certified = 'n';
      if (newVal != null && newVal !== '') {
        certified = 'y';
      }
      this.updateStoreFormData({
        data: certified,
        main: this.getProductIdSelected,
        sub: 'traffic_certified',
      });
    },
    setTrafficSignature(newVal) {
      this.updateStoreFormData({
        data: newVal,
        main: this.getProductIdSelected,
        sub: 'traffic_signature',
      });
    },

    setTrafficSignatureCertified(newVal) {
      this.updateStoreFormData({
        data: newVal,
        main: this.getProductIdSelected,
        sub: 'traffic_signature_certified',
      });
    },
  },
  data() {
    return {
      trafficLabel: `
        To comply with FCC regulations, please certify the circuit by selecting and initialing one of the following statements which best describes the traffic on private line.
      `,
      trafficTypeDescription: `
        Interstate traffic is traffic that crosses state boundaries. Internet, by its inherent nature, is considered interstate by the FCC.

        Intrastate traffic is traffic that is contained within a single state.
      `,
      hint: {
        interstate: 'The traffic will be Interstate. Meaning more than ten percent (10%) of the traffic that will be transmitted over this will be interstate in nature.',
        intrastate: 'The traffic will be Intrastate. Meaning The circuit listed above is Intrastate and constitutes ten percent (10%) or less of the total traffic on this.',
      },
      selected: "",
      traffic_signature: "",
      traffic_signature_certified: 'n',
    };
  },
  computed: {
    ...mapGetters([
      'getProductIdSelected',
      'getProductInfo',
      'isRetail',
      'getCompanyData',
    ]),
    initialLabel() {
      let label = '';
      if (this.selected === 'intrastate') {
        label = 'I acknowledge and certify that the interstate traffic (which also includes Internet and international traffic) constitutes ten percent (10%) or less of the total traffic on this. ';
      } else if (this.selected === 'interstate') {
        label = 'I acknowledge and certify that more than ten percent (10%) of the traffic to be transmitted over this circuit is and will be interstate (including Internet and international traffic) in nature. ';
      }
      label += 'By initialing here, you certify the circuit';
      if (this.getProductInfo().traffic != null) {
        label += ` is ${this.getProductInfo().traffic}`;
      }
      return label;
    },
    getNote() {
      const { traffic } = this.getProductInfo();
      if (traffic) {
        return `Note: ${this.hint[traffic]}`;
      }
      return '';
    },
    isUsfExempt() {
      const dateStr = this.getCompanyData?.usf_waiver_date;
      if (!dateStr) {
        return false;
      }

      const today = new Date().toISOString().slice(0, 10);
      const currentYear = new Date().getFullYear();
      const waiverYear = new Date(dateStr).getFullYear();

      const result = dateStr >= today || waiverYear === currentYear;
      return result;
    },
  },
  watch: {
    traffic_signature(newVal) {
      this.setTrafficSignature(newVal);
    },

    traffic_signature_certified(newVal) {
      this.setTrafficSignatureCertified(newVal);
    },
  },
  components: {
    MoreInfoToolTip,
    ReviewItem,
    ESignature,
  },
};
</script>
